import React from "react";
import { PageTopNav } from "@/components/nav/PageTopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { editSystemUserSchema } from "@tyto/shared";
import { Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { type FC } from "react";
import { z } from "zod";

interface User {
  id: string;
  email: string;
  name: string;
  systemAdmin: boolean;
  teamId?: string;
  createdAt: string;
  updatedAt: string;
}

type EditSystemUserFormValues = z.infer<typeof editSystemUserSchema>;

export const AdminEditUser: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: ['user', userId],
    queryFn: async () => {
      const res = await fetch(`/api/user/${userId}`);
      if (!res.ok) throw new Error('Failed to fetch user');
      return res.json();
    },
    enabled: !!userId,
  });

  const form = useForm<EditSystemUserFormValues>({
    resolver: zodResolver(editSystemUserSchema),
    defaultValues: {
      email: '',
      name: '',
    },
  });

  // Prefill form when user is loaded
  React.useEffect(() => {
    if (user) {
      form.reset({
        email: user.email,
        name: user.name,
      });
    }
  }, [user, form]);

  const updateUserMutation = useMutation({
    mutationFn: async (data: EditSystemUserFormValues) => {
      const response = await fetch(`/api/user/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      form.reset();
      navigate('/app/admin/users');
    },
  });

  const onSubmit = (data: EditSystemUserFormValues) => {
    updateUserMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64 text-red-600">
        Failed to load user.
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto mt-8">
      <PageTopNav pageName="Edit User" />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="user@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="User name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/app/admin/users')}>Cancel</Button>
            <Button type="submit" disabled={updateUserMutation.isPending}>
              {updateUserMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update'
              )}
            </Button>
          </div>
          {updateUserMutation.isError && (
            <div className="text-red-600 text-sm mt-2">
              {updateUserMutation.error instanceof Error ? updateUserMutation.error.message : 'Failed to update user'}
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};
